// src/hooks/useAudioPlayer.ts
import { useCallback, useEffect, useRef, useState } from "react";

import { TTS_TAIL_CONFIG } from "../utils/constants";

interface UseAudioPlayerOptions {
    autoPlay?: boolean;
    onError?: (error: Error | Event) => void;
    onEnded?: () => void;
    onPlay?: () => void;
    onPause?: () => void;
}

// 辅助函数：向 DataView 写入字符串（模块级）
const writeString = (view: DataView, offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
    }
};

// PCM → WAV（用于本地预览播放，TTS 流式路径不走这里）
const pcmToWav = (
    pcmData: Uint8Array,
    sampleRate = TTS_TAIL_CONFIG.SAMPLE_RATE,
): Blob => {
    const dataLength = pcmData.length;
    const buffer = new ArrayBuffer(44 + dataLength);
    const view = new DataView(buffer);

    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + dataLength, true);
    writeString(view, 8, "WAVE");

    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);

    writeString(view, 36, "data");
    view.setUint32(40, dataLength, true);

    new Uint8Array(buffer).set(pcmData, 44);

    return new Blob([buffer], { type: "audio/wav" });
};

// 解码 Base64 为二进制（显式 ArrayBuffer 构造，满足 BlobPart 类型）
const decodeBase64ToBytes = (base64Str: string): Uint8Array<ArrayBuffer> => {
    let cleanBase64 = base64Str;
    if (base64Str.startsWith("data:audio/pcm;base64,")) {
        cleanBase64 = base64Str.replace("data:audio/pcm;base64,", "");
    } else if (base64Str.startsWith("data:audio/mp3;base64,")) {
        cleanBase64 = base64Str.replace("data:audio/mp3;base64,", "");
    } else if (base64Str.startsWith("data:audio/wav;base64,")) {
        cleanBase64 = base64Str.replace("data:audio/wav;base64,", "");
    }
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(new ArrayBuffer(binaryString.length));
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
};

const isBase64PCM = (str: string): boolean => {
    return str.startsWith("AAAA") || str.startsWith("data:audio/pcm");
};

export const useAudioPlayer = (options?: UseAudioPlayerOptions) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | Event | null>(null);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const currentUrlRef = useRef<string | null>(null);

    // ===== 流式播放队列：每收到一个 tts_chunk 就入队，按顺序播放 =====
    const queueRef = useRef<string[]>([]);
    const playingRef = useRef(false);
    // 标记流式 TTS 是否结束（flushQueue 调用后置 true，此时队列空才可触发 onEnded）
    const streamingDoneRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    const cleanupCurrentAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.oncanplaythrough = null;
            audioRef.current.onerror = null;
            audioRef.current.ontimeupdate = null;
            audioRef.current.onended = null;
            audioRef.current.pause();
            audioRef.current.src = "";
            audioRef.current.load();
            audioRef.current = null;
        }
        if (
            currentUrlRef.current &&
            currentUrlRef.current.startsWith("blob:")
        ) {
            URL.revokeObjectURL(currentUrlRef.current);
        }
        currentUrlRef.current = null;
    }, []);

    const reportError = useCallback((err: Error | Event) => {
        setError(err);
        optionsRef.current?.onError?.(err);
    }, []);

    // ========== 播放下一个队列项 ==========
    const playNextInQueue = useCallback(async (): Promise<void> => {
        const next = queueRef.current.shift();
        if (!next) {
            if (streamingDoneRef.current) {
                // 仅在 streaming_done=true 时（flushQueue 已调用）才真正播完
                playingRef.current = false;
                setIsPlaying(false);
                optionsRef.current?.onEnded?.();
            }
            // 否则只是暂时队列空了，等待后续 tts_chunk 到达
            return;
        }
        playingRef.current = true;
        setIsPlaying(true);

        try {
            cleanupCurrentAudio();
            setIsLoading(true);

            const audio = new Audio();
            audioRef.current = audio;

            let audioUrl: string;
            // tts_chunk 是 mp3 base64（后端 TTS format=mp3）
            if (next.startsWith("data:audio/mp3;base64,")) {
                audioUrl = next;
            } else if (next.startsWith("AAAA") || next.startsWith("data:audio/pcm")) {
                // PCM base64 → WAV blob
                const pcmBytes = decodeBase64ToBytes(next);
                const wavBlob = pcmToWav(pcmBytes, TTS_TAIL_CONFIG.SAMPLE_RATE);
                audioUrl = URL.createObjectURL(wavBlob);
            } else if (
                next.startsWith("blob:") ||
                next.startsWith("http") ||
                next.startsWith("data:audio/wav")
            ) {
                audioUrl = next;
            } else {
                // 默认当 mp3 base64
                audioUrl = `data:audio/mp3;base64,${next}`;
            }
            currentUrlRef.current = audioUrl;
            audio.src = audioUrl;
            audio.volume = 1.0;
            audio.preload = "auto";
            // 重置播放进度为 0：避免某些浏览器从非零位置开始播放，造成首字丢失
            audio.currentTime = 0;

            // 等待"loadeddata"而不是"canplay"：loadeddata 表示音频首帧数据已被浏览器解码完成
            // canplay 触发时机过早，可能只加载了 header，实际音频首帧未就绪，play() 时会跳过前几毫秒导致首字丢失
            await new Promise<void>((resolve, reject) => {
                const timeout = window.setTimeout(() => {
                    // 超时兜底：如果事件没触发也继续播放，避免卡死
                    console.warn("音频加载事件超时，兜底开始播放");
                    resolve();
                }, 10000);
                const onReady = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                const onFailed = (e: unknown) => {
                    clearTimeout(timeout);
                    reject(typeof e === "string" ? new Error(e) : e);
                };
                // 优先用 loadeddata（首帧已解码），兼容 canplaythrough / loadedmetadata
                audio.onloadeddata = onReady;
                audio.oncanplaythrough = onReady;
                // data URL 不走网络请求，加一个 canplay 兜底（loadeddata 不触发时也能继续）
                audio.oncanplay = () => {
                    // canplay 触发后再等 20ms，确保首帧数据真正加载完成
                    window.setTimeout(() => {
                        clearTimeout(timeout);
                        resolve();
                    }, 20);
                };
                audio.onerror = onFailed;
                audio.load();
            });

            // 播放前再次强制 currentTime=0，双重防止首字丢失
            try { audio.currentTime = 0; } catch { /* ignore */ }
            setDuration(audio.duration || 0);
            await audio.play();
            setIsLoading(false);
            optionsRef.current?.onPlay?.();

            audio.ontimeupdate = () => {
                setCurrentTime(audio.currentTime || 0);
            };
            audio.onended = () => {
                // 播放下一块
                cleanupCurrentAudio();
                void playNextInQueue();
            };
            audio.onerror = (e) => {
                const err = typeof e === "string" ? new Error(e) : e;
                reportError(err);
                setIsPlaying(false);
                setIsLoading(false);
                cleanupCurrentAudio();
                // 出错也继续下一块，避免队列卡死
                void playNextInQueue();
            };
        } catch (err) {
            console.error("❌ 队列播放失败:", err);
            const normalizedError =
                err instanceof Error ? err : new Error(String(err));
            reportError(normalizedError);
            setIsPlaying(false);
            setIsLoading(false);
            cleanupCurrentAudio();
            // 继续下一块
            void playNextInQueue();
        }
    }, [cleanupCurrentAudio, reportError]);

    // ========== 流式入队（核心：tts_chunk 调用此方法） ==========
    const enqueueAudio = useCallback(
        (base64Audio: string) => {
            // 有新音频到达，重置"流结束"标记，允许 onEnded 在真正播完后再触发
            streamingDoneRef.current = false;
            queueRef.current.push(base64Audio);
            if (!playingRef.current) {
                void playNextInQueue();
            }
        },
        [playNextInQueue],
    );

    // ========== 流式播放完成（标记流入结束，让 playNextInQueue 在队列空时触发 onEnded） ==========
    const flushQueue = useCallback(() => {
        // 标记不再有新数据：playNextInQueue 在队列空时将真正播完
        streamingDoneRef.current = true;
        // 若当前队列为空，立即触发 onEnded（否则等最后一块播完后再触发）
        if (!playingRef.current && queueRef.current.length === 0) {
            optionsRef.current?.onEnded?.();
        }
    }, []);

    // ========== 单次播放（兼容旧接口） ==========
    const play = useCallback(
        async (audioData: string | Blob | Uint8Array): Promise<boolean> => {
            try {
                // 清空队列
                queueRef.current = [];
                playingRef.current = false;
                streamingDoneRef.current = false;
                cleanupCurrentAudio();

                setIsLoading(true);
                setError(null);

                let audioUrl: string;

                if (audioData instanceof Blob) {
                    audioUrl = URL.createObjectURL(audioData);
                } else if (audioData instanceof Uint8Array) {
                    const wavBlob = pcmToWav(audioData, TTS_TAIL_CONFIG.SAMPLE_RATE);
                    audioUrl = URL.createObjectURL(wavBlob);
                } else if (typeof audioData === "string") {
                    if (isBase64PCM(audioData)) {
                        const pcmBytes = decodeBase64ToBytes(audioData);
                        const wavBlob = pcmToWav(pcmBytes, TTS_TAIL_CONFIG.SAMPLE_RATE);
                        audioUrl = URL.createObjectURL(wavBlob);
                    } else if (audioData.startsWith("data:audio/mp3;base64,")) {
                        audioUrl = audioData;
                    } else if (
                        audioData.startsWith("blob:") ||
                        audioData.startsWith("http")
                    ) {
                        audioUrl = audioData;
                    } else {
                        audioUrl = `data:audio/mp3;base64,${audioData}`;
                    }
                } else {
                    throw new Error("不支持的音频数据格式");
                }

                const audio = new Audio();
                audioRef.current = audio;
                currentUrlRef.current = audioUrl;
                audio.preload = "auto";
                audio.volume = 1.0;
                audio.src = audioUrl;
                // 重置播放进度为 0，避免某些浏览器首字丢失
                audio.currentTime = 0;

                await new Promise<void>((resolve, reject) => {
                    const timeout = window.setTimeout(() => {
                        console.warn("单次播放加载超时，兜底开始播放");
                        resolve();
                    }, 15000);
                    const onReady = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                    const onFailed = (e: unknown) => {
                        clearTimeout(timeout);
                        reject(typeof e === "string" ? new Error(e) : e);
                    };
                    // 优先用 loadeddata（首帧已解码），canplaythrough 作备份
                    audio.onloadeddata = onReady;
                    audio.oncanplaythrough = onReady;
                    // canplay 兜底并额外等待 20ms 确保首帧就绪
                    audio.oncanplay = () => {
                        window.setTimeout(() => {
                            clearTimeout(timeout);
                            resolve();
                        }, 20);
                    };
                    audio.onerror = onFailed;
                    audio.load();
                });

                // 播放前再次强制回到 0，双重防止首字丢失
                try { audio.currentTime = 0; } catch { /* ignore */ }
                setDuration(audio.duration || 0);
                await audio.play();
                setIsPlaying(true);
                setIsLoading(false);
                playingRef.current = true;
                optionsRef.current?.onPlay?.();

                audio.ontimeupdate = () => {
                    setCurrentTime(audio.currentTime || 0);
                };
                audio.onended = () => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                    playingRef.current = false;
                    optionsRef.current?.onEnded?.();
                    cleanupCurrentAudio();
                };
                audio.onerror = (e) => {
                    const err = typeof e === "string" ? new Error(e) : e;
                    setError(err);
                    setIsPlaying(false);
                    setIsLoading(false);
                    playingRef.current = false;
                    optionsRef.current?.onError?.(err);
                    cleanupCurrentAudio();
                };

                return true;
            } catch (err) {
                console.error("❌ play 失败:", err);
                const normalizedError =
                    err instanceof Error ? err : new Error(String(err));
                setIsPlaying(false);
                setIsLoading(false);
                reportError(normalizedError);
                cleanupCurrentAudio();
                return false;
            }
        },
        [cleanupCurrentAudio, reportError],
    );

    const pause = useCallback(() => {
        if (audioRef.current && isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
            playingRef.current = false;
            optionsRef.current?.onPause?.();
        }
    }, [isPlaying]);

    const stop = useCallback(() => {
        queueRef.current = [];
        playingRef.current = false;
        streamingDoneRef.current = false;
        pause();
        cleanupCurrentAudio();
    }, [pause, cleanupCurrentAudio]);

    const toggle = useCallback(() => {
        if (isPlaying) {
            pause();
        } else if (currentUrlRef.current) {
            void play(currentUrlRef.current);
        }
    }, [isPlaying, pause, play]);

    useEffect(() => {
        return () => {
            queueRef.current = [];
            cleanupCurrentAudio();
        };
    }, [cleanupCurrentAudio]);

    return {
        play,
        pause,
        stop,
        toggle,
        isPlaying,
        isLoading,
        error,
        duration,
        currentTime,
        audioRef,
        enqueueAudio,
        flushQueue,
    };
};

export default useAudioPlayer;
